import { ServicePermissionsPicker, ServicePermissionsSummary } from '../components/service-permissions.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientDto, CreateUserInput, Paginated, ResellerDto, UserDto, CustomerServicePermission } from '@cpm/shared';
import { Plus, Trash2, UserCog } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
} from '../components/ui.js';

function CreateUserForm({ onDone, customerOnly = false }: { onDone: () => void; customerOnly?: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<CreateUserInput['role']>(customerOnly ? 'client' : 'staff');
  const [clientId, setClientId] = useState('');
  const [accountAdmin, setAccountAdmin] = useState(false);
  const [servicePermissions, setServicePermissions] = useState<CustomerServicePermission[]>([]);
  const [resellerId, setResellerId] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(customerOnly);

  // Only fetched once the client role is selected.
  const clients = useQuery({
    queryKey: ['clients', { forUserPicker: true }],
    queryFn: () => api.get<Paginated<ClientDto>>('/api/clients/?page=1&perPage=100'),
    enabled: role === 'client',
  });

  // Only fetched once the reseller role is selected (G20).
  const resellers = useQuery({
    queryKey: ['resellers', { forUserPicker: true }],
    queryFn: () => api.get<ResellerDto[]>('/api/resellers'),
    enabled: role === 'reseller',
  });

  const create = useMutation({
    mutationFn: (input: CreateUserInput) => api.post<UserDto>(customerOnly ? '/api/resellers/portal-users' : '/api/auth/users', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
  });

  const selectRole = (next: CreateUserInput['role']) => {
    setRole(next);
    // Onboarding default: hand out a temporary password the tenant/reseller
    // must change at first login (G15/G20).
    if (next === 'client' || next === 'reseller') setMustChangePassword(true);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({
          name,
          email,
          password,
          role,
          clientId: role === 'client' ? clientId : undefined,
          resellerId: role === 'reseller' ? resellerId : undefined,
          mustChangePassword,
          accountAdmin: role === 'client' && accountAdmin,
          servicePermissions: role === 'client' && !accountAdmin ? servicePermissions : [],
        });
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="user-name">Name</Label>
        <Input id="user-name" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="user-email">Email</Label>
        <Input id="user-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="user-password">Password</Label>
        <Input
          id="user-password"
          type="password"
          required
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">At least 12 characters.</p>
      </div>
      {!customerOnly && <div>
        <Label htmlFor="user-role">Role</Label>
        <Select id="user-role" value={role} onChange={(e) => selectRole(e.target.value as CreateUserInput['role'])}>
          <option value="staff">Staff — manage clients and subscriptions</option>
          <option value="admin">Admin — full control incl. settings</option>
          <option value="reseller">Reseller — scoped panel for one reseller</option>
          <option value="client">Client — portal for one customer</option>
        </Select>
      </div>}
      {role === 'client' && (
        <div>
          <Label htmlFor="user-client">Client</Label>
          {clients.isPending ? (
            <Spinner />
          ) : clients.error ? (
            <ErrorNote error={clients.error} />
          ) : (
            <Select
              id="user-client"
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="" disabled>
                Select the tenant this login belongs to…
              </option>
              {clients.data.items.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                  {client.company ? ` (${client.company})` : ''}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
      {role === 'client' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={accountAdmin} onChange={(e) => setAccountAdmin(e.target.checked)} />Customer account administrator</label>}
      {role === 'client' && !accountAdmin && <ServicePermissionsPicker value={servicePermissions} onChange={setServicePermissions} disabled={create.isPending} />}
      {role === 'reseller' && (
        <div>
          <Label htmlFor="user-reseller">Reseller</Label>
          {resellers.isPending ? (
            <Spinner />
          ) : resellers.error ? (
            <ErrorNote error={resellers.error} />
          ) : resellers.data.length === 0 ? (
            <p className="text-sm text-slate-500">Create a reseller first (Resellers page).</p>
          ) : (
            <Select
              id="user-reseller"
              required
              value={resellerId}
              onChange={(e) => setResellerId(e.target.value)}
            >
              <option value="" disabled>
                Select the reseller this login operates…
              </option>
              {resellers.data.map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={mustChangePassword}
          onChange={(e) => setMustChangePassword(e.target.checked)}
        />
        <span>
          Require password change at first login
          <span className="block text-xs text-slate-500 dark:text-slate-400">
            Treat the password above as temporary — the user must pick their own before they can
            use the {role === 'client' ? 'portal' : 'panel'}.
          </span>
        </span>
      </label>
      {create.error != null && <ErrorNote error={create.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={create.isPending}>
          Create user
        </Button>
      </div>
    </form>
  );
}

export function UsersPage({ customerOnly = false }: { customerOnly?: boolean }) {
  const queryClient = useQueryClient();
  const me = useMe();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [permissions, setPermissions] = useState<CustomerServicePermission[]>([]);
  const userPath = customerOnly ? '/api/resellers/portal-users' : '/api/auth/users';

  const users = useQuery({
    queryKey: ['users', customerOnly],
    queryFn: () => api.get<UserDto[]>(userPath),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: { isActive?: boolean; role?: UserDto['role']; accountAdmin?: boolean; servicePermissions?: CustomerServicePermission[] } }) =>
      api.patch<UserDto>(`${userPath}/${id}`, input),
    onSuccess: () => { setEditing(null); void queryClient.invalidateQueries({ queryKey: ['users'] }); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/users/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const resetTwoFactor = useMutation({
    mutationFn: (id: string) => api.post(`/api/auth/users/${id}/reset-2fa`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const revokeSessions = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/users/${id}/sessions`),
  });

  const mutationError = update.error ?? remove.error ?? resetTwoFactor.error ?? revokeSessions.error;

  return (
    <div>
      <PageHeader
        title={customerOnly ? "Customer users" : "Users"}
        description={customerOnly ? "Manage logins and delegated services for your customer accounts." : "Panel operators and client portal logins."}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New user
          </Button>
        }
      />
      {mutationError != null && (
        <div className="mb-4">
          <ErrorNote error={mutationError} />
        </div>
      )}

      <Card>
        {users.isPending ? (
          <Spinner />
        ) : users.error ? (
          <div className="p-5">
            <ErrorNote error={users.error} />
          </div>
        ) : users.data.length === 0 ? (
          <EmptyState icon={<UserCog className="size-10" />} title="No users" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Last login</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.data.map((user) => {
                  const isSelf = user.id === me.data?.id;
                  return (
                    <tr key={user.id}>
                      <td className="px-5 py-3">
                        <p className="font-medium">
                          {user.name}
                          {isSelf && <span className="ml-1.5 text-xs text-slate-400">(you)</span>}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                        {user.role === 'client' && !user.accountAdmin && <ServicePermissionsSummary value={user.servicePermissions ?? []} />}
                      </td>
                      <td className="px-5 py-3">
                        <Badge
                          tone={
                            user.role === 'admin'
                              ? 'blue'
                              : user.role === 'reseller'
                                ? 'green'
                                : user.role === 'client'
                                  ? 'yellow'
                                  : 'gray'
                          }
                        >
                          {user.role === 'client' && user.accountAdmin ? 'Account administrator' : user.role}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <span className="flex flex-wrap gap-1.5">
                          <Badge tone={user.isActive ? 'green' : 'red'}>
                            {user.isActive ? 'active' : 'disabled'}
                          </Badge>
                          {user.twoFactorEnabled && <Badge tone="blue">2FA</Badge>}
                          {user.mustChangePassword && (
                            <Badge tone="yellow">temp password</Badge>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-5 py-3">
                        {!isSelf && (
                          <div className="flex flex-wrap justify-end gap-1">
                            {user.role === 'client' && !user.accountAdmin && <Button variant="ghost" onClick={() => { setEditing(user); setPermissions(user.servicePermissions ?? []); }}>Service permissions</Button>}
                            {user.role === 'client' && <Button variant="ghost" onClick={() => update.mutate({ id: user.id, input: { accountAdmin: !user.accountAdmin } })}>{user.accountAdmin ? 'Make member' : 'Make account administrator'}</Button>}
                            {!customerOnly && user.twoFactorEnabled && (
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Reset two-factor for ${user.email}? This also signs them out everywhere.`,
                                    )
                                  ) {
                                    resetTwoFactor.mutate(user.id);
                                  }
                                }}
                              >
                                Reset 2FA
                              </Button>
                            )}
                            {!customerOnly && <Button
                              variant="ghost"
                              onClick={() => {
                                if (window.confirm(`Sign ${user.email} out of all sessions?`)) {
                                  revokeSessions.mutate(user.id);
                                }
                              }}
                            >
                              Sign out
                            </Button>}
                            <Button
                              variant="secondary"
                              onClick={() =>
                                update.mutate({ id: user.id, input: { isActive: !user.isActive } })
                              }
                            >
                              {user.isActive ? 'Disable' : 'Enable'}
                            </Button>
                            {!customerOnly && <Button
                              variant="ghost"
                              className="hover:!text-red-600"
                              aria-label={`Delete ${user.email}`}
                              onClick={() => {
                                if (window.confirm(`Delete user ${user.email}?`)) remove.mutate(user.id);
                              }}
                            >
                              <Trash2 className="size-4" />
                            </Button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal title="Service permissions" open={!!editing} onClose={() => setEditing(null)}>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); if (editing) update.mutate({ id: editing.id, input: { servicePermissions: permissions } }); }}>
          <p className="font-medium">{editing?.name}</p><ServicePermissionsPicker value={permissions} onChange={setPermissions} disabled={update.isPending} />
          {update.error && <ErrorNote error={update.error} />}<div className="flex gap-2"><Button type="submit" disabled={update.isPending}>Save permissions</Button><Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button></div>
        </form>
      </Modal>
      <Modal title="New user" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CreateUserForm customerOnly={customerOnly} onDone={() => setCreateOpen(false)} />
      </Modal>
    </div>
  );
}
